import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
  createTask,
  pollTaskUntilComplete,
  extractTaskOutputText,
  extractTaskOutputFiles,
  parseTaskOutputJson,
  ManusApiErrors,
} from "../../../api/manus";
import {
  isManusEnabled,
  shouldFallbackToMock,
  generateRequestId as generateLegacyRequestId,
  debugLog,
  getManusConfig,
} from "../../../utils/manusConfig";
import {
  checkDailyLimit,
  deductDailyUsage,
  generateRequestId as generateDailyRequestId,
} from "../../../utils/dailyUsageManager";
import {
  generatePriceEstimation,
  formatCurrency,
} from "../utils/aiPriceUtils";
import {
  parseManusMarkdownReport,
  downloadFileContent,
  createMarkdownFileFromText,
} from "../../../utils/manusMarkdownParser";
import { ref, set } from "firebase/database";
import { db } from "../../../firebase/config";

const RENTAL_PROPERTY_TYPE_LABELS = {
  apartment: "Căn hộ cho thuê",
  private_house: "Nhà riêng cho thuê",
  shophouse: "Nhà phố/Shophouse cho thuê",
  villa: "Biệt thự cho thuê",
  office: "Văn phòng cho thuê",
  retail: "Mặt bằng kinh doanh cho thuê",
  warehouse: "Kho/xưởng cho thuê",
  land: "Đất cho thuê",
  other: "BĐS khác cho thuê",
};

const formatLine = (label, value, suffix = "") =>
  value ? `${label}: ${value}${suffix}` : "";

const joinDetails = (lines) => lines.filter(Boolean).join("\n");

const buildRentalPrompt = (formData) => {
  const propertyType =
    RENTAL_PROPERTY_TYPE_LABELS[formData.propertyType] ||
    `${formData.propertyType || "BĐS"} cho thuê`;

  const baseDetails = [
    formatLine("Loại BĐS", propertyType),
    formatLine("Địa chỉ", formData.address),
    formatLine("Dự án", formData.projectName),
    formatLine("Toà/Khu", formData.building),
    formatLine("Tầng/Dãy", formData.floor),
    formatLine("Mã căn", formData.apartmentCode),
  ];

  const type = formData.propertyType;

  const isApartment =
    type === "apartment" ||
    type === "Căn hộ" ||
    type === "Căn hộ chung cư";

  const isHouse =
    type === "private_house" ||
    type === "low_rise_project" ||
    type === "Nhà thổ cư" ||
    type === "Nhà riêng";

  if (isApartment) {
    baseDetails.push(
      formatLine("Loại căn hộ", formData.apartmentType),
      formatLine("Phòng ngủ", formData.bedrooms),
      formatLine("Phòng tắm", formData.bathrooms),
      formatLine("Diện tích", formData.area, " m²"),
      formatLine("Hướng ban công", formData.balconyDirection)
    );
  } else if (isHouse) {
    baseDetails.push(
      formatLine("Loại nhà", formData.houseType),
      formatLine("Phòng ngủ", formData.bedrooms),
      formatLine("Phòng tắm", formData.bathrooms),
      formatLine("Diện tích đất", formData.landArea, " m²"),
      formatLine(
        "Diện tích sử dụng",
        formData.constructionArea || formData.area,
        " m²"
      ),
      formatLine("Mặt tiền", formData.frontage, " m"),
      formatLine("Số tầng", formData.floors),
      formatLine("Hướng cửa", formData.doorDirection)
    );
  } else {
    baseDetails.push(
      formatLine("Diện tích", formData.area || formData.landArea, " m²"),
      formatLine("Mặt tiền", formData.frontage, " m"),
      formatLine("Chiều sâu", formData.landLength, " m"),
      formatLine("Hướng", formData.doorDirection || formData.landDirection)
    );
  }

  baseDetails.push(
    formatLine("Tình trạng pháp lý", formData.legalStatus),
    formatLine("Tình trạng nội thất", formData.furnitureStatus),
    formatLine("Thời gian bàn giao", formData.availableFrom),
    formatLine("Yêu cầu thêm", formData.notes)
  );

  const detailSection = joinDetails(baseDetails);

  const locationContext = formData.address
    ? formData.address.split(',').slice(-2).join(',').trim()
    : 'Việt Nam';

  const currentYear = new Date().getFullYear();
  const isNewProperty = formData.constructionYear && formData.constructionYear >= currentYear - 3;

  // Determine target tenant profile based on property type and size
  const getTenantProfile = () => {
    if (isApartment) {
      if (formData.bedrooms <= 2) {
        return `
### **Đối tượng thuê phù hợp nhất**:
- **Nhóm chính**: Cặp đôi trẻ, gia đình nhỏ 2-3 người, người độc thân có thu nhập ổn định
- **Thu nhập**: Từ [X-Y] triệu/tháng (giá thuê chiếm 25-35% thu nhập)
- **Nghề nghiệp**: Nhân viên văn phòng, chuyên gia, expat trẻ
- **Nhu cầu**: Gần công ty, trường học, không gian yên tĩnh`;
      } else {
        return `
### **Đối tượng thuê phù hợp nhất**:
- **Nhóm chính**: Gia đình 4-6 người, expat, người nước ngoài
- **Thu nhập**: Từ [X] triệu/tháng trở lên
- **Nghề nghiệp**: Quản lý, chuyên gia nước ngoài, gia đình 2 nguồn thu nhập
- **Nhu cầu**: Không gian rộng, trường quốc tế, tiện ích đầy đủ`;
      }
    } else if (type === 'office') {
      return `
### **Đối tượng thuê phù hợp nhất**:
- **Nhóm chính**: Công ty nhỏ 5-15 người, startup, doanh nghiệp vừa
- **Ngân sách**: [X-Y] triệu/tháng
- **Nhu cầu**: Vị trí đẹp, giao thông thuận tiện, hạ tầng IT ổn định`;
    } else {
      return `
### **Đối tượng thuê**:
- Phân tích cụ thể đối tượng thuê phù hợp với loại BĐS này
- Thu nhập/Ngân sách: [X-Y] triệu
- Nhu cầu đặc biệt: [Phân tích theo loại hình]`;
    }
  };

  const legalStatusNote = formData.legalStatus
    ? formData.legalStatus === 'Sổ hồng/Sổ đỏ'
      ? '- **Điểm cộng**: Sổ hồng/sổ đỏ rõ ràng - Tăng độ tin cậy với khách thuê'
      : formData.legalStatus === 'Hợp đồng mua bán'
      ? '- **Lưu ý**: HĐMB - Có thể ảnh hưởng niềm tin khách thuê, cần giải thích rõ'
      : '- **Cảnh báo**: Pháp lý chưa rõ - Cần minh bạch với khách thuê'
    : '';

  return `
🏢 **VAI TRÒ**: Bạn là chuyên gia cho thuê bất động sản hàng đầu tại Việt Nam với hơn 20 năm kinh nghiệm, chuyên tư vấn cho cả chủ nhà và nhà đầu tư về định giá cho thuê tối ưu tại khu vực ${locationContext}.

📋 **THÔNG TIN BẤT ĐỘNG SẢN CHO THUÊ**:

${detailSection}
${legalStatusNote}

---

⚠️ **CHỈ THỊ BẮT BUỘC - ĐỌC KỸ TRƯỚC KHI BẮT ĐẦU**:

1. **KHÔNG ĐƯỢC HỎI THÊM**: Bạn ĐÃ CÓ đầy đủ thông tin ở trên. TUYỆT ĐỐI KHÔNG được hỏi thêm bất kỳ câu hỏi nào hay yêu cầu người dùng cung cấp thêm dữ liệu. Hãy phân tích dựa trên thông tin hiện có.

2. **PHẢI TẠO FILE MARKDOWN**: Sau khi hoàn thành phân tích, bạn BẮT BUỘC phải sử dụng công cụ tạo file (create_file/write_file) để tạo một file markdown (.md) với tên "bao_cao_gia_cho_thue_[timestamp].md" chứa TOÀN BỘ nội dung báo cáo.

3. **HOÀN THÀNH NGAY**: Đây là yêu cầu một lần (one-shot). Hãy phân tích hoàn chỉnh và tạo file ngay. KHÔNG được dừng lại giữa chừng hay đợi xác nhận.

4. **NỘI DUNG ĐẦY ĐỦ**: Báo cáo phải bao gồm TẤT CẢ 3 phương pháp định giá cho thuê, kể cả khi một số phương pháp không áp dụng được (ghi rõ "Không áp dụng" và giải thích lý do).

5. **KHÔNG NHẮC TỪ "MANUS"**: Kết quả trả về không được chứa bất kỳ từ khóa "Manus" nào.

---

🎯 **YÊU CẦU PHÂN TÍCH CHI TIẾT (1500-2500 từ)**:

## 1️⃣ PHÂN TÍCH THỊ TRƯỜNG CHO THUÊ KHU VỰC

- **Nhu cầu thuê**: [Cao/Trung bình/Thấp] với lý do cụ thể
- **Nguồn cung**: [Nhiều/Vừa phải/Ít] BĐS cùng phân khúc đang cho thuê
- **Tỷ lệ lấp đầy (Occupancy Rate)**: Trung bình khu vực ~[X%]
- **Thời gian trống (Vacancy Period)**: Thường [X] tuần/tháng giữa các lần thuê
- **Mức độ cạnh tranh**: [Cao/TB/Thấp] - [XX] BĐS tương tự trong bán kính 1-2km

### **Xu hướng giá thuê**:
- 12 tháng qua: [Tăng/Giảm/Ổn định] [X%]
- Dự báo 6-12 tháng: [Xu hướng] dựa trên [lý do: cung cầu, chính sách, mùa vụ]
- Mùa cao điểm: [Tháng X-Y] (thường đầu năm học, sau Tết)
- Mùa trầm: [Tháng X-Y]

## 2️⃣ ĐỊNH GIÁ CHO THUÊ BẤT ĐỘNG SẢN - PHÂN TÍCH ĐẦY ĐỦ 3 PHƯƠNG PHÁP

**LƯU Ý QUAN TRỌNG**: Mỗi phương pháp định giá cho thuê cần được phân tích cụ thể và đầy đủ. Tất cả 3 phương pháp đều BẮT BUỘC phải có trong báo cáo.

---

### **🔍 PHƯƠNG PHÁP 1: PHƯƠNG PHÁP SO SÁNH (Market Comparison Approach)**

#### **Nguyên lý**:
Định giá cho thuê dựa trên giá thuê thực tế của các BĐS tương tự đang/đã cho thuê trong cùng khu vực.

#### **Bảng so sánh với BĐS cho thuê tương tự** (tìm 3-5 BĐS thực tế):

| STT | Địa chỉ gần đúng | Diện tích | Nội thất | Giá thuê | Đơn giá/m²/tháng | Thời gian đăng | Tình trạng | Điểm khác biệt | Hệ số điều chỉnh | Giá sau điều chỉnh |
|-----|------------------|-----------|----------|----------|-----------------|----------------|------------|----------------|------------------|-------------------|
| 1 | [Địa chỉ 1] | [XX] m² | [Đầy đủ/CB] | [X] tr/th | [Y] k/m²/th | [X tuần trước] | [Đã thuê/Trống] | [View tốt hơn +3%, tầng thấp -2%] | [1.01] | [Z] tr/th |
| 2 | [Địa chỉ 2] | [XX] m² | [Đầy đủ/CB] | [X] tr/th | [Y] k/m²/th | [Y tuần trước] | [Đã thuê/Trống] | [Diện tích lớn hơn -3%, mới hơn +2%] | [0.99] | [Z] tr/th |
| 3 | [Địa chỉ 3] | [XX] m² | [Đầy đủ/CB] | [X] tr/th | [Y] k/m²/th | [Z tuần trước] | [Đã thuê/Trống] | [Tương tự, cùng tòa] | [1.00] | [Z] tr/th |

#### **Các yếu tố điều chỉnh**:
- **Nội thất**: Đầy đủ/Cơ bản/Không [±10-20%]
- **Vị trí trong toà/khu vực**: [±3-7%]
- **Tầng/View**: [±2-5%]
- **Tình trạng mới/cũ**: [±3-5%]
- **Diện tích**: [±2-5%] (diện tích lớn có đơn giá/m² thấp hơn)
- **Thời gian đăng**: [±2%] (giá có thể giảm theo thời gian)

#### **Kết quả PPSS**:
- **Giá thuê trung bình sau điều chỉnh**: **[X] triệu VNĐ/tháng**
- **Khoảng giá hợp lý theo PPSS**: **[X - Y] triệu/tháng**
- **Đơn giá/m²**: **[Z] nghìn VNĐ/m²/tháng**
- **Độ tin cậy**: [Cao/TB/Thấp] - Phụ thuộc số lượng mẫu so sánh

**Phân tích**: BĐS này [rẻ hơn/đắt hơn/ngang] mặt bằng chung khoảng [X%] do [lý do: vị trí, nội thất, tình trạng...]

**Lưu ý**: Ưu tiên BĐS đã cho thuê thành công trong 2-3 tháng gần nhất. BĐS đang rao có thể cao hơn giá thực tế 5-10%.

---

### **💰 PHƯƠNG PHÁP 2: PHƯƠNG PHÁP THU NHẬP (Income Approach) - TỪ GÓC ĐỘ NHÀ ĐẦU TƯ**

#### **Nguyên lý**:
Tính giá thuê hợp lý dựa trên tỷ suất sinh lời đầu tư (Rental Yield) mà nhà đầu tư kỳ vọng từ BĐS này.

#### **Phân tích thu nhập**:

**A. Giá trị BĐS ước tính** (nếu mua):
- **Giá mua/Giá trị thị trường**: [X] tỷ VNĐ
  * Nguồn: Giá giao dịch tương tự hoặc ước tính thị trường
  * Nếu là chủ nhà, dùng giá mua hoặc giá định giá hiện tại

**B. Tỷ suất sinh lời kỳ vọng (Expected Rental Yield)**:
- **Gross Rental Yield khu vực**: [4-7%]/năm
  * Chung cư trung tâm: 4-5%/năm
  * Chung cư ngoại ô: 5-6%/năm
  * Nhà riêng: 5-7%/năm
  * Văn phòng/Shophouse: 6-8%/năm
- **Yield áp dụng**: [X%]/năm (dựa trên loại BĐS và rủi ro)

**C. Giá thuê hợp lý từ Yield**:

**Công thức**:
\`\`\`
Giá thuê tháng = (Giá trị BĐS × Yield%) / 12
\`\`\`

**Tính toán**:
- **Giá thuê = ([X] tỷ × [Y%]) / 12**
- **Giá thuê theo PPTN**: **[Z] triệu/tháng**

**D. Kiểm chứng với Net Rental Yield**:

**Chi phí vận hành ước tính** (hàng năm):
| Khoản chi | Số tiền/năm | % trên giá thuê gô |
|-----------|-------------|-------------------|
| Phí quản lý (nếu chung cư) | [X] tr | [~3-5%] |
| Bảo trì, sửa chữa | [X] tr | [~5-8%] |
| Thuế (nếu đăng ký) | [X] tr | [~10%] |
| Vacancy cost (1-2 tháng trống/năm) | [X] tr | [~8-15%] |
| Chi phí khác | [X] tr | [~2%] |
| **TỔNG CHI PHÍ** | **[TỔNG]** tr | **[Z%]** |

**Net Rental Income**:
- **Gross Income**: [Giá thuê × 12] = [X] triệu/năm
- **Net Income**: [X - Chi phí] = [Y] triệu/năm
- **Net Rental Yield**: [Y / Giá trị BĐS] = [Z%]/năm

**E. Kết quả PPTN**:
- **Giá thuê hợp lý theo PPTN**: **[X] triệu/tháng**
- **Đảm bảo Gross Yield**: ~[Y%]/năm
- **Đảm bảo Net Yield**: ~[Z%]/năm (sau chi phí)
- **Độ tin cậy**: [Cao/TB/Thấp] - Phụ thuộc độ chính xác giá trị BĐS

**Kết luận**: Với giá thuê [X] triệu/tháng, nhà đầu tư sẽ có tỷ suất sinh lời [Y%]/năm (gross) hoặc [Z%]/năm (net), [phù hợp/cao hơn/thấp hơn] mức kỳ vọng thị trường.

---

### **🏗️ PHƯƠNG PHÁP 3: PHƯƠNG PHÁP CHI PHÍ (Cost Approach)**

#### **Nguyên lý**:
Tính giá thuê dựa trên tổng chi phí đầu tư và kỳ vọng thu hồi vốn của chủ nhà/nhà đầu tư.

#### **Phân tích chi phí**:

**A. Tổng đầu tư ban đầu**:

1. **Giá trị BĐS**: [X] tỷ VNĐ
   - Nếu là chủ nhà: Giá mua/giá trị hiện tại
   - Nếu ước tính: Dựa trên giá thị trường

2. **Chi phí nội thất/cải tạo** (nếu có):
   | Hạng mục | Chi phí |
   |----------|---------|
   | Nội thất cơ bản (giường, tủ, bàn ghế) | [X] triệu |
   | Điều hòa, máy giặt, tủ lạnh | [X] triệu |
   | Sơn sửa, cải tạo | [X] triệu |
   | Rèm, đèn, trang trí | [X] triệu |
   | **TỔNG** | **[Y]** triệu |

3. **Tổng vốn đầu tư**:
   - **TỔNG = Giá BĐS + Chi phí nội thất** = **[Z] tỷ VNĐ**

**B. Chi phí vốn & Kỳ vọng thu hồi**:

1. **Chi phí cơ hội vốn**:
   - **Lãi suất ngân hàng/cơ hội đầu tư khác**: [4-8%]/năm
   - **Chi phí vốn hàng năm**: [Z tỷ × X%] = **[Y] triệu/năm**

2. **Khấu hao nội thất**:
   - **Tuổi thọ nội thất**: [5-7] năm
   - **Khấu hao hàng năm**: [Chi phí nội thất / Tuổi thọ] = **[X] triệu/năm**

3. **Chi phí vận hành & Rủi ro**:
   | Khoản chi | Số tiền/năm |
   |-----------|-------------|
   | Phí quản lý | [X] triệu |
   | Bảo trì | [X] triệu |
   | Thuế (10% doanh thu) | [X] triệu |
   | Vacancy (1-2 tháng) | [X] triệu |
   | **TỔNG** | **[Y]** triệu |

4. **Tổng chi phí hàng năm**:
   - **TỔNG = Chi phí vốn + Khấu hao + Vận hành** = **[Z] triệu/năm**

**C. Tính giá thuê tối thiểu**:

**Công thức**:
\`\`\`
Giá thuê tối thiểu/tháng = Tổng chi phí năm / 12
\`\`\`

**Tính toán**:
- **Giá thuê tối thiểu = [Z] triệu / 12** = **[X] triệu/tháng**

**D. Giá thuê hợp lý (có lợi nhuận)**:

Để có lợi nhuận hợp lý ([10-20%] trên chi phí):
- **Lợi nhuận mong muốn**: [X%] = [Y] triệu/năm
- **Thu nhập cần thiết**: [Tổng chi phí + Lợi nhuận] = [Z] triệu/năm
- **Giá thuê hợp lý**: [Z / 12] = **[A] triệu/tháng**

**E. Kết quả PPCP**:
- **Giá thuê tối thiểu (hòa vốn)**: **[X] triệu/tháng**
- **Giá thuê hợp lý (có lãi 15%)**: **[Y] triệu/tháng**
- **Đơn giá/m²**: **[Z] nghìn VNĐ/m²/tháng**
- **Độ tin cậy**: [Cao/TB/Thấp] - Phụ thuộc độ chính xác chi phí

**Kết luận**: Chủ nhà cần cho thuê tối thiểu [X] triệu/tháng để không lỗ, và [Y] triệu/tháng để có lợi nhuận hợp lý [Z%]/năm.

---

### **📈 TỔNG HỢP KẾT QUẢ 3 PHƯƠNG PHÁP**

| Phương pháp | Giá thuê ước tính | Trọng số | Giá có trọng số | Độ tin cậy | Ghi chú |
|-------------|-------------------|----------|----------------|------------|---------|
| PP So sánh | [X-Y] tr/th | [50%] | [Z] tr/th | Cao | Dựa trên [N] BĐS tham chiếu |
| PP Thu nhập (Yield) | [X] tr/th | [30%] | [Y] tr/th | TB | Yield khu vực [A%]/năm |
| PP Chi phí | [X] tr/th (min) - [Y] tr/th (lãi 15%) | [20%] | [Z] tr/th | TB | Đảm bảo lợi nhuận chủ nhà |
| **TỔNG TRỌNG SỐ** | | **100%** | **[TỔNG]** tr/th | | |

**Lưu ý về trọng số**: 
- PP So sánh có trọng số cao nhất vì phản ánh trực tiếp thị trường
- PP Thu nhập kiểm chứng tính hợp lý cho nhà đầu tư
- PP Chi phí đảm bảo lợi ích tối thiểu cho chủ nhà

### **🎯 GIÁ CHO THUÊ KHUYẾN NGHỊ**

- **Khoảng giá hợp lý**: **[X - Y] triệu VNĐ/tháng** (dựa trên tổng hợp 3 phương pháp)
  * **Mức thấp [X] triệu**: Cho thuê nhanh, BĐS có nhược điểm nhỏ, thị trường trầm
  * **Mức trung bình [TB] triệu**: Giá thị trường chuẩn, phù hợp nhất
  * **Mức cao [Y] triệu**: BĐS có điểm cộng đặc biệt (view, nội thất sang, vị trí đắc địa)

- **Đơn giá/m²**: **[Z] nghìn VNĐ/m²/tháng**

- **Giá khuyến nghị chính**: **[A] triệu VNĐ/tháng** (giá hợp lý nhất)

**Giải thích lựa chọn giá**: [Tại sao chọn mức giá này dựa trên kết quả 3 phương pháp, điều kiện thị trường, ưu nhược điểm BĐS]

**Kiểm chứng**:
- Gross Rental Yield: [Giá thuê × 12 / Giá BĐS] = **[X%]/năm** - [Phù hợp/Cao/Thấp] so với khu vực
- Payback period (Hòa vốn nội thất): ~[Y] năm - [Hợp lý/Dài]

## 3️⃣ PHÂN TÍCH ĐỐI TƯỢNG THUÊ
${getTenantProfile()}

### **Đối tượng phụ** (nếu có):
- [Nhóm khách hàng thứ 2 có thể quan tâm]

### **Thời gian thuê trung bình**:
- **Dự kiến**: [6 tháng / 1 năm / 2 năm / >2 năm]
- **Lý do**: [Đặc điểm đối tượng quyết định thời hạn]

## 4️⃣ PHÂN TÍCH TÀI CHÍNH CHO CHỦ NHÀ/ĐẦU TƯ

### **Thu nhập cho thuê**:
- **Giá thuê đề xuất**: [X] triệu/tháng
- **Thu nhập/năm**: [X * 12] = [Y] triệu/năm

### **Chi phí vận hành ước tính** (hàng tháng):

| Khoản chi | Chi phí (VNĐ/tháng) | Ghi chú |
|-----------|---------------------|---------|
| Phí quản lý (nếu chung cư) | [X] | ~[Y]% giá thuê |
| Bảo trì, sửa chữa | [X] | Tích lũy 5-10% giá thuê |
| Thuế TNCN (10% doanh thu) | [X] | Nếu đăng ký hoặc rủi ro |
| Chi phí marketing/môi giới | [X] | ~0.5-1 tháng/lần tìm khách |
| Vacancy cost | [X] | Ước tính [X] tuần trống/năm |
| Khấu hao nội thất | [X] | Nếu full nội thất |
| **TỔNG** | **[TỔNG]** | |

### **Thu nhập ròng**:
- **Net Rental Income**: [Thu - Chi] = [X] triệu/tháng
- **Net Rental Yield**: ~[X%]/năm

### **Phân tích ROI cho nhà đầu tư**:
- **Gross Rental Yield**: ~[X%]/năm
- **Net Rental Yield**: ~[Y%]/năm (sau chi phí)
- **Rental Yield TB khu vực**: ~[Z%]/năm

## 5️⃣ PHÂN TÍCH ĐIỂM MẠNH/YẾU CHO THUÊ

### ✅ **ĐIỂM CỘNG KHI CHO THUÊ** (5+ điểm):
1. [Vị trí gần công ty/trường/bệnh viện...]
2. [Giao thông thuận - gần metro/bus...]
3. [An ninh tốt - bảo vệ 24/7...]
4. [Tiện ích - hồ bơi/gym/siêu thị...]
${formData.furnitureStatus === 'Nội thất đầy đủ' ? '5. Nội thất đầy đủ, vào ở ngay - RẤT HẤP DẪN' : ''}
${isNewProperty ? '6. BĐS mới, chưa qua sử dụng' : ''}
7. [Không gian thoáng, view đẹp...]

### ⚠️ **ĐIỂM TRỪ/KHÓ KHĂN** (3+ điểm, trung thực):
1. [Nếu có: Xa trung tâm...]
2. [Nếu có: Không nội thất - cần bỏ thêm chi phí]
3. [Nếu có: Cũ, cần sửa...]
${formData.furnitureStatus === 'Không có nội thất' ? '4. Trống, khách phải tự sắm - Giảm hấp dẫn với khách ngắn hạn' : ''}

### **Đánh giá tổng quan**:
- **Độ hấp dẫn cho thuê**: [X/10] ⭐
- **Thời gian cho thuê dự kiến**: [X tuần - Y tháng]
- **Tỷ lệ thành công**: [Cao/TB/Thấp]

## 6️⃣ ĐIỀU KIỆN HỢP ĐỒNG ĐỀ XUẤT

### **Thời hạn hợp đồng**:
- **Đề xuất**: [6 tháng / 1 năm / 2 năm] dựa trên:
  * Đối tượng thuê: [Gia đình/expat → 1-2 năm; Độc thân → 6 tháng-1 năm]
  * Hợp đồng dài = thu nhập ổn định, ít rủi ro trống
- **Gia hạn**: Tăng giá [0-5]%/năm

### **Tiền cọc & Thanh toán**:
- **Tiền cọc**: [1-2] tháng (theo quy định)
- **Chu kỳ thanh toán**:
  * **Đề xuất**: [Tháng / Quý / Năm]
  * Tháng: Linh hoạt, phù hợp khách ngắn hạn
  * Quý/Năm: Giảm rủi ro nợ, giảm [5-10%] giá để khuyến khích

### **Phân bổ chi phí**:
- **Điện, nước, internet**: [Khách/Chủ/Chia sẻ]
- **Phí quản lý**: [Chủ/Khách]
- **Bảo trì lớn**: Chủ nhà
- **Sửa nhỏ (lỗi sử dụng)**: Khách thuê

### **Điều khoản đặc biệt**:
- **Nuôi thú cưng**: [Có/Không] - Nếu có, tăng cọc [0.5-1] tháng
- **Số người tối đa**: [X người]
- **Đăng ký tạm trú**: [Có hỗ trợ/Không]
- **Hủy sớm**: Penalty [X] tháng nếu break trước hạn

## 7️⃣ CHIẾN LƯỢC CHO THUÊ TỐI ƯU

### **A. Giá khởi điểm & Room**:
- **Giá niêm yết**: [X] triệu (cao hơn target [5-10%])
- **Giá target**: [Y] triệu (giá thực muốn)
- **Giá đáy**: [Z] triệu (không nhận dưới mức này)

### **B. Thời điểm tốt nhất**:
- **Mùa cao điểm**: [Tháng 1-2 sau Tết, 7-8 năm học mới, 10-11]
- **Tránh**: Tháng 12 (cuối năm), Tết
- **Tip**: Đăng [Thứ 2-3], sáng [8-10h]

### **C. Kênh marketing**:
1. **Online**: Batdongsan.com.vn (TOP 1), Mogi.vn, Nha.vn, Chotot.com
2. **Social**: Group FB/Zalo "Cho thuê [Khu vực]"
${isApartment && formData.bedrooms <= 2 ? '3. **Expat groups**: "Expats in [City]", "Housing in [City]"' : ''}
4. **Môi giới**: Sàn địa phương (phí 0.5-1 tháng)
5. **Biển quảng cáo**: Tại toà nhà/khu vực

### **D. Cách trình bày tin đăng**:
**Tiêu đề**: "[Loại] [X]m² • [Y PN] • [Nội thất] • Gần [Landmark] • [Giá] tr/th"

**Điểm nhấn**:
1. ✨ [Vị trí đắc địa]
2. 🏠 [Không gian rộng, thoáng]
3. 🛋️ [Nội thất hiện đại/Mới 100%]
4. 🚇 [Gần metro/trường/bệnh viện]
5. 💰 [Giá tốt khu vực]

### **E. Lời khuyên ảnh & video**:
- **Số lượng**: 15-20 ảnh chất lượng cao
- **Nội dung**: Phòng khách, PN, bếp, WC, ban công, view, tiện ích chung
- **Video**: Walkthrough 1-2 phút (tăng 30-40% tỷ lệ xem)
- **Thời gian**: Sáng/chiều (ánh sáng tự nhiên)
- **Staging**: Dọn gọn, bỏ đồ cá nhân, cây xanh

### **F. Ưu đãi thu hút** (nếu cần nhanh):
- [ ] Miễn 1 tháng (nếu thuê 12 tháng)
- [ ] Miễn phí dịch vụ tháng đầu
- [ ] Tặng gói internet/TV
- [ ] Hỗ trợ chi phí sơn sửa
- [ ] Flexible move-in date

## 8️⃣ RỦI RO & GIẢM THIỂU

### **Rủi ro khi cho thuê**:
1. **Rủi ro trống (Vacancy)**:
   - Ước tính: [X] tuần/năm
   - Tác động: Mất [Y] triệu/năm
   - Giảm thiểu: Giữ khách cũ, marketing trước 1-2 tháng

2. **Rủi ro khách xấu**:
   - Nợ tiền, phá hoại, làm ồn
   - Giảm thiểu: Screening kỹ (xem bên dưới)

3. **Rủi ro pháp lý**:
   - Tranh chấp hợp đồng
   - Giảm thiểu: HĐ rõ ràng, công chứng nếu cần

### **Biện pháp screening khách thuê**:
✅ **Checklist**:
- [ ] CMND/CCCD hợp lệ
- [ ] Giấy xác nhận công việc/HĐLĐ
- [ ] Bảng lương 3 tháng (thu nhập ≥ 3-4x giá thuê)
- [ ] Liên hệ người thân/Bảo lãnh
- [ ] Lịch sử thuê trước (nếu có)
- [ ] Check mạng xã hội (FB/LinkedIn)
- [ ] Gặp trực tiếp, quan sát thái độ

### **Bảo vệ quyền lợi chủ nhà**:
- **HĐ chuẩn**: Mẫu theo Luật Nhà ở, công chứng nếu giá trị lớn
- **Biên bản bàn giao**: Chụp ảnh chi tiết tình trạng, nội thất
- **Bảo hiểm**: Cân nhắc bảo hiểm tài sản cho thuê
- **Giữ liên lạc**: Check-in 3-6 tháng/lần

---

## 📊 TÓM TẮT NHANH

| Tiêu chí | Đánh giá |
|----------|----------|
| **Giá thuê hợp lý** | **[X - Y] triệu/tháng** |
| **Đơn giá/m²** | **[XXX,XXX] VNĐ/m²/tháng** |
| **Xu hướng thuê** | [Tăng/Ổn định/Giảm] |
| **Nhu cầu khu vực** | [Cao ⚡/TB 📊/Thấp 🐌] |
| **Thời gian cho thuê** | [X tuần - Y tháng] |
| **Đối tượng chính** | [Gia đình/Expat/Công ty...] |
| **Thời hạn HĐ đề xuất** | [6 tháng / 1 năm / 2 năm] |
| **Gross Yield** | ~[X%]/năm |
| **Net Yield** | ~[Y%]/năm |
| **Độ hấp dẫn** | [X/10] ⭐ |

---

## 🎯 KHUYẾN NGHỊ CUỐI

**TÓM TẮT 3 BƯỚC THÀNH CÔNG**:

1. **Định giá đúng**: Niêm yết [X] triệu, sẵn sàng đàm phán đến [Y] triệu
2. **Marketing tốt**: Đăng [tháng X-Y], ảnh đẹp, mô tả hấp dẫn, nhiều kênh
3. **Screening kỹ**: Chọn khách uy tín, HĐ rõ ràng, bàn giao cẩn thận

**Dự báo**: Với chiến lược trên, BĐS này có khả năng cho thuê thành công trong [X tuần - Y tháng], đạt giá [Z] triệu/tháng, tạo thu nhập ổn định ~[A%] yield/năm.

**Lưu ý**: Giá thuê cuối phụ thuộc đàm phán thực tế. Nên khảo sát trực tiếp. Linh hoạt điều chỉnh trong 2-3 tuần đầu theo phản hồi.

---

🚨 **HÀNH ĐỘNG BẮT BUỘC - ĐỌC KỸ VÀ THỰC HIỆN NGAY**:

**BƯỚC 1**: Phân tích hoàn chỉnh theo cấu trúc trên (1500-2500 từ)

**BƯỚC 2**: SAU KHI PHÂN TÍCH XONG, BẠN PHẢI:
- ✅ **BẮT BUỘC** sử dụng công cụ tạo file (create_file/write_file/Create tool)
- ✅ Tên file: "bao_cao_gia_cho_thue_[timestamp].md"
- ✅ Định dạng: Markdown (.md)
- ✅ Nội dung: TOÀN BỘ báo cáo chi tiết bao gồm:
  * Tất cả 3 phương pháp định giá cho thuê (đầy đủ bảng số liệu, tính toán)
  * Bảng tổng hợp kết quả
  * Phân tích thị trường, đối tượng thuê
  * Chiến lược cho thuê, quản lý rủi ro
  * Khuyến nghị cụ thể
- ✅ Độ dài: 1500-2500 từ
- ❌ KHÔNG được chỉ trả về text thông thường
- ❌ KHÔNG được hỏi thêm câu hỏi hay yêu cầu xác nhận
- ❌ KHÔNG được chứa từ "Manus" trong kết quả

**BƯỚC 3**: Kết thúc ngay sau khi tạo file thành công

---

⚠️ **LƯU Ý QUAN TRỌNG**:
- Nếu thiếu một số thông tin cụ thể (như giá thuê tham chiếu), hãy ước tính dựa trên kinh nghiệm thị trường và ghi chú rõ "ước tính"
- Nếu một phương pháp không áp dụng được, ghi rõ "Không áp dụng" và giải thích lý do
- Tuyệt đối KHÔNG dừng lại giữa chừng để hỏi thêm thông tin
- Đây là yêu cầu tự động, người dùng KHÔNG thể trả lời câu hỏi của bạn
- Nếu gặp bất kỳ yêu cầu xác thực (robot, OTP, captcha...), bỏ qua và tiếp tục xử lý

🎯 **MỤC TIÊU**: Tạo một file báo cáo markdown hoàn chỉnh, chi tiết, chuyên nghiệp về định giá cho thuê bất động sản dựa trên thông tin đã cung cấp ở trên.
`;
};

// Function to save rental analysis result to Firebase Realtime Database
const saveToFirebase = async (
  userId,
  taskId,
  analysisResult,
  formData,
  completedTask,
  manusTaskId = null,  // ✅ ADD: Manus task ID (từ Manus API)
  manusKeyId = null    // ✅ ADD: Manus key ID
) => {
  try {
    if (!userId || !taskId) {
      console.warn("Missing userId or taskId, skipping Firebase save");
      return;
    }

    const historyData = {
      taskId,
      request_id: taskId,
      user_id: userId,
      message: buildRentalPrompt(formData),
      status: "completed",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      current_task_title: "Yêu cầu định giá cho thuê",
      task_url: completedTask?.metadata?.task_url || "https://manus.im/app",
      credit_usage: completedTask?.credit_usage || null,
      model: completedTask?.model || null,
      manus_task_id: manusTaskId || completedTask?.task_id || null, // ✅ FIX: Lưu Manus task ID
      manus_key_id: manusKeyId || null,                              // ✅ FIX: Lưu Manus key ID
      manus_response: {
        task: completedTask,
        outputText: analysisResult.manusAnalysis,
        parsedJson: analysisResult.manusParsed,
        markdown: analysisResult.manusMarkdown || null,
      },
      analysis_result: {
        estimatedPrice: analysisResult.estimatedPrice,
        pricePerM2: analysisResult.pricePerM2,
        marketTrend: analysisResult.marketTrend,
        confidence: analysisResult.confidence,
        marketInsights: analysisResult.marketInsights,
        recommendations: analysisResult.recommendations,
        summary: analysisResult.summary,
      },
      form_data: formData,
    };

    const historyRef = ref(db, `histories/${userId}/${taskId}`);
    await set(historyRef, historyData);

    console.log("✅ [Firebase] Saved rental analysis result to histories:", {
      userId,
      taskId,
      manusTaskId,
      manusKeyId,
      status: "completed",
    });

    return true;
  } catch (error) {
    console.error("❌ [Firebase] Error saving rental analysis to histories:", error);
    return false;
  }
};

const buildRentalResult = ({
  parsedJson,
  rawText,
  fallback,
  task,
  markdownData = null,
}) => {
  const json = parsedJson || {};

  const formatRange = (range) => {
    if (!range || typeof range.min !== "number" || typeof range.max !== "number") {
      return null;
    }
    return `${formatCurrency(range.min)} - ${formatCurrency(range.max)}`;
  };

  // Nếu có markdown data (Manus trả về file), ưu tiên sử dụng
  if (markdownData && markdownData.success && markdownData.data) {
    const md = markdownData.data;

    return {
      estimatedPrice: md.price_estimate?.formatted || fallback.estimatedPrice,
      pricePerM2: md.price_per_m2?.formatted || fallback.pricePerM2,
      marketTrend: md.market_trend || fallback.marketTrend,
      confidence: md.confidence || fallback.confidence,
      marketInsights:
        Array.isArray(md.insights) && md.insights.length > 0
          ? md.insights
          : fallback.marketInsights,
      recommendations:
        Array.isArray(md.recommendations) && md.recommendations.length > 0
          ? md.recommendations
          : [],
      summary: md.summary || "",
      manusAnalysis: rawText,
      manusMarkdown: markdownData.rawMarkdown,
      manusTask: task,
      manusParsed: md,
    };
  }

  // Fallback: parse JSON như cũ
  const estimatedPrice =
    formatRange(json.rent_estimate) || fallback.estimatedPrice;

  const pricePerM2 =
    formatRange(json.rent_per_m2) || fallback.pricePerM2;

  return {
    estimatedPrice,
    pricePerM2,
    marketTrend: json.market_trend || fallback.marketTrend,
    confidence: json.confidence || fallback.confidence,
    marketInsights: Array.isArray(json.insights) && json.insights.length
      ? json.insights
      : fallback.marketInsights,
    recommendations:
      Array.isArray(json.recommendations) && json.recommendations.length
        ? json.recommendations
        : [],
    summary: json.summary || "",
    manusAnalysis: rawText,
    manusTask: task,
    manusParsed: json,
  };
};

export const useManusRentalAnalysis = (formData) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState("");
  const [manusResponse, setManusResponse] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const [error, setError] = useState(null);

  const resetAnalysis = useCallback(() => {
    setIsAnalyzing(false);
    setAnalysisProgress("");
    setManusResponse(null);
    setUploadedFiles([]);
    setCurrentRequestId(null);
    setCurrentTaskId(null);
    setTaskStatus(null);
    setError(null);
  }, []);

  const handleAnalyzeRentalPrice = useCallback(
    async (formValidation, onPriceResult) => {
      if (!formValidation?.isValid) {
        const message =
          formValidation?.errors?.[0] ||
          "Vui lòng điền đầy đủ thông tin bắt buộc!";
        toast.error(message, {
          position: "top-right",
          autoClose: 3000,
          icon: "⚠️",
        });
        return;
      }

      const userId = localStorage.getItem("userId");
      const requestId = generateDailyRequestId("rental_analysis");

      try {
        setIsAnalyzing(true);
        setAnalysisProgress("🔍 Đang kiểm tra giới hạn sử dụng...");
        setError(null);
        setCurrentRequestId(requestId);

        const limitInfo = await checkDailyLimit(userId);

        if (!limitInfo.success) {
          const limitError = new Error(
            limitInfo.message || "Bạn đã hết lượt sử dụng hôm nay"
          );
          limitError.reason = limitInfo.reason;
          limitError.limit = limitInfo.limit;
          limitError.used = limitInfo.used;
          throw limitError;
        }

        // 📌 CREATE PENDING RECORD IMMEDIATELY (before Manus processing)
        setAnalysisProgress("📝 Tạo yêu cầu định giá cho thuê...");

        // ✅ FIX: Tạo requestId cho hệ thống (dùng để lưu Firebase history)
        // Đây là ID nội bộ, KHÔNG phải taskId từ Manus
        const internalRequestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setCurrentTaskId(internalRequestId); // Tạm thời set internal ID, sẽ update sau khi có Manus task_id

        const pendingRecord = {
          taskId: internalRequestId, // Internal ID cho Firebase key
          request_id: internalRequestId,
          user_id: userId,
          message: buildRentalPrompt(formData),
          status: "pending",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          current_task_title: "Yêu cầu định giá cho thuê",
          form_data: formData,
          manus_task_id: null, // Sẽ được cập nhật sau khi call Manus API
          manus_key_id: null,  // Sẽ được cập nhật sau khi call Manus API
        };

        // Save pending record to Firebase immediately
        try {
          const pendingRef = ref(db, `histories/${userId}/${internalRequestId}`);
          await set(pendingRef, pendingRecord);
          console.log("✅ [Firebase] Created rental pending record:", { userId, internalRequestId });
        } catch (firebaseError) {
          console.error("❌ [Firebase] Error creating rental pending record:", firebaseError);
        }

        // 📌 CALL CALLBACK IMMEDIATELY to trigger navigation
        onPriceResult?.({
          estimatedPrice: "Đang xử lý...",
          pricePerM2: "Đang xử lý...",
          marketTrend: "N/A",
          confidence: "N/A",
          marketInsights: [],
          recommendations: [],
          summary: "Yêu cầu đang chờ xử lý",
          manusAnalysis: "Hệ thống đang xử lý yêu cầu của bạn...",
        });

        // 📌 BACKGROUND PROCESSING (after callback)
        setAnalysisProgress("🚀 Đang gửi yêu cầu cho hệ thống AI...");

        // Start background processing in a non-blocking way
        (async () => {
          try {
            if (!isManusEnabled()) {
              if (!shouldFallbackToMock()) {
                throw new Error(
                  "Chức năng Manus đang tạm thời tắt. Vui lòng thử lại sau."
                );
              }

              const fallbackResult = generatePriceEstimation(formData);
              return;
            }

            // ✅ FIX: GỌI MANUS API TRƯỚC để lấy task_id thật
            const prompt = buildRentalPrompt(formData);
            const config = getManusConfig();
            const manualTaskId =
              config.taskMode === "agent" ? generateLegacyRequestId("task") : null;

            console.log("🚀 [Manus] Creating rental task on Manus API...", {
              manualTaskId: manualTaskId || "auto-generated by Manus",
            });

            const taskCreation = await createTask({
              prompt,
              taskId: manualTaskId,
            });

            // ✅ FIX: Lấy task_id THẬT từ Manus response (ĐÂY MỚI LÀ ID QUAN TRỌNG)
            const manusTaskId = taskCreation?.task_id; // "7vSGpKzM8uKrCLF6fbx6UK"
            const manusKeyId = taskCreation?._keyId; // "env_key"

            if (!manusTaskId) {
              throw new Error("Không nhận được task_id từ Manus API");
            }

            // ✅ Validate keyId - this is critical for polling
            if (!manusKeyId) {
              console.error("❌ [Manus] ERROR: _keyId not received from task creation!", {
                taskCreation,
              });
              throw new Error(
                "Không nhận được _keyId từ Manus. Không thể theo dõi task với API key chính xác. " +
                "Vui lòng kiểm tra cấu hình proxy server."
              );
            }

            console.log("✅ [Manus] Rental task created successfully:", {
              internalRequestId, // ID nội bộ của hệ thống
              manusTaskId,       // ID thật từ Manus (dùng để polling)
              manusKeyId,        // Key ID để đảm bảo polling đúng API key
              taskCreationFull: taskCreation,
            });

            // ✅ FIX: Update Firebase với MANUS TASK ID thật
            // Giữ nguyên internal ID làm key, nhưng lưu manus_task_id để tracking
            const trackingRef = ref(db, `histories/${userId}/${internalRequestId}`);
            await set(trackingRef, {
              taskId: internalRequestId,      // Internal ID (Firebase key)
              request_id: internalRequestId,
              user_id: userId,
              message: buildRentalPrompt(formData),
              status: "processing",
              created_at: pendingRecord.created_at,
              updated_at: new Date().toISOString(),
              current_task_title: "Yêu cầu định giá cho thuê",
              form_data: formData,
              manus_task_id: manusTaskId, // ✅ Manus task ID THẬT (dùng để polling)
              manus_key_id: manusKeyId,   // ✅ Key ID để tracking
            });

            console.log("✅ [Firebase] Updated rental task with Manus task info:", {
              internalRequestId,
              manusTaskId,
              manusKeyId,
            });

            // ✅ FIX: Đợi Manus index task (8-10 giây)
            console.log("⏳ [Manus] Waiting 8 seconds for task to be indexed...");
            await new Promise(resolve => setTimeout(resolve, 8000));

            // ✅ FIX: Polling với manusTaskId THẬT (không phải internalRequestId)
            console.log("🔄 [Manus] Starting polling rental task with correct task ID:", {
              manusTaskId,
              manusKeyId,
            });

            setAnalysisProgress("⏳ Hệ thống AI đang phân tích giá cho thuê...");

            const completedTask = await pollTaskUntilComplete(manusTaskId, {
              keyId: manusKeyId, // ✅ Dùng đúng key đã tạo task
              onStatus: ({ status, attempt }) => {
                setTaskStatus(status);
                setAnalysisProgress(
                  status === "running"
                    ? `⏱️ Hệ thống AI đang xử lý (lần kiểm tra ${attempt})...`
                    : `⏱️ Trạng thái hiện tại: ${status}`
                );
              },
            });

            const rawText = extractTaskOutputText(completedTask);
            const outputFiles = extractTaskOutputFiles(completedTask);

            // Kiểm tra xem có file markdown không
            let markdownData = null;
            let fallbackFileCreated = false;
            
            if (outputFiles && outputFiles.length > 0) {
              setAnalysisProgress("📄 Đang tải file báo cáo từ Manus...");
              const markdownFile = outputFiles.find(
                (f) =>
                  f.mimeType === "text/markdown" || f.fileName?.endsWith(".md")
              );

              if (markdownFile) {
                console.log("📄 Found markdown file:", markdownFile.fileName);
                const downloadResult = await downloadFileContent(
                  markdownFile.fileUrl
                );

                if (downloadResult.success) {
                  console.log("✅ Downloaded markdown successfully");
                  markdownData = parseManusMarkdownReport(downloadResult.content);
                  console.log("📊 Parsed markdown data:", markdownData);
                } else {
                  console.warn("⚠️ Failed to download markdown:", downloadResult.error);
                }
              }
            }

            // 🔧 FALLBACK: Nếu không có file markdown, tạo file từ output text
            if (!markdownData && rawText) {
              console.log("⚠️ No markdown file from Manus, creating fallback file for rental analysis...");
              setAnalysisProgress("📝 Đang tạo file báo cáo cho thuê từ kết quả phân tích...");
              
              const fallbackFile = createMarkdownFileFromText(
                rawText,
                `bao_cao_gia_cho_thue_${Date.now()}.md`
              );

              if (fallbackFile.success) {
                console.log("✅ Created fallback rental markdown file:", fallbackFile.fileName);
                
                // Parse markdown data từ rawText
                markdownData = parseManusMarkdownReport(rawText);
                fallbackFileCreated = true;

                // Thêm fallback file vào outputFiles để lưu
                outputFiles.push({
                  fileUrl: fallbackFile.fileUrl,
                  fileName: fallbackFile.fileName,
                  mimeType: fallbackFile.mimeType,
                  isFallback: true, // Đánh dấu là file fallback
                });

                console.log("📊 Parsed fallback rental markdown data:", markdownData);
              } else {
                console.error("❌ Failed to create fallback rental markdown file:", fallbackFile.error);
              }
            }

            const parsed = parseTaskOutputJson(completedTask);
            const fallbackResult = generatePriceEstimation(formData);
            const analysisResult = buildRentalResult({
              parsedJson: parsed.data,
              rawText,
              fallback: fallbackResult,
              task: completedTask,
              markdownData,
            });

            setManusResponse({
              task: completedTask,
              outputText: rawText,
              parsedJson: parsed.data,
              markdownData: markdownData?.rawMarkdown || null,
            });

            // Lưu uploaded files vào state
            setUploadedFiles(
              outputFiles.map((f) => ({
                filename: f.fileName,
                cloudinary_url: f.fileUrl,
                format: f.mimeType,
              }))
            );

            // ✅ FIX: Lưu vào Firebase với cả 2 ID
            setAnalysisProgress("💾 Đang lưu kết quả vào cơ sở dữ liệu...");
            const saveSuccess = await saveToFirebase(
              userId,
              internalRequestId, // ✅ Internal ID (Firebase key)
              analysisResult,
              formData,
              completedTask,
              manusTaskId,       // ✅ Manus task ID (để tracking)
              manusKeyId         // ✅ Manus key ID (để tracking)
            );

            if (saveSuccess) {
              setAnalysisProgress("✅ Đã lưu kết quả thành công!");
            } else {
              setAnalysisProgress("⚠️ Hoàn thành nhưng lưu kết quả thất bại");
            }

            setAnalysisProgress("✅ Đã hoàn thành định giá thuê với hệ thống AI");

            // ✅ QUAN TRỌNG: CHỈ TRỪ LƯỢT KHI REQUEST THÀNH CÔNG HOÀN TOÀN
            // Đảm bảo rằng Manus task đã completed và kết quả đã được lưu vào Firebase
            setAnalysisProgress("🔢 Đang trừ lượt sử dụng...");
            const usageResult = await deductDailyUsage(
              userId,
              requestId,
              "rental_analysis"
            );

            if (usageResult.success) {
              toast.success(
                `Phân tích thành công! Còn ${usageResult.remaining}/${usageResult.limit} lượt hôm nay.`,
                {
                  position: "top-right",
                  autoClose: 4000,
                  icon: "✅",
                }
              );
            } else {
              console.warn("⚠️ [Usage] Failed to deduct usage after successful rental analysis:", usageResult);
            }
          } catch (backgroundError) {
            debugLog("Rental background processing error", backgroundError);
            setError(backgroundError);

            // ❌ NẾU CÓ LỖI → KHÔNG TRỪ LƯỢT
            // Chỉ log ra để admin có thể theo dõi
            console.error("❌ [Rental Analysis] Request failed, usage NOT deducted:", {
              userId,
              requestId,
              error: backgroundError.message,
            });

            // ✅ FIX: Update error status với internalRequestId
            try {
              const errorRef = ref(db, `histories/${userId}/${internalRequestId}`);
              await set(errorRef, {
                taskId: internalRequestId,
                request_id: internalRequestId,
                user_id: userId,
                message: buildRentalPrompt(formData),
                status: "failed",
                created_at: pendingRecord.created_at,
                updated_at: new Date().toISOString(),
                current_task_title: "Yêu cầu định giá cho thuê",
                form_data: formData,
                error: backgroundError.message || "Lỗi xử lý",
              });
            } catch (firebaseErrorUpdate) {
              console.error("❌ [Firebase] Failed to update rental error status:", firebaseErrorUpdate);
            }

            if (backgroundError.code === ManusApiErrors.API_KEY_MISSING) {
              toast.error(
                "Thiếu Manus API key. Vui lòng cấu hình REACT_APP_MANUS_API_KEY.",
                {
                  position: "top-right",
                  autoClose: 5000,
                }
              );
            } else {
              toast.error(
                backgroundError.message || "Lỗi khi phân tích giá thuê với Manus",
                {
                  position: "top-right",
                  autoClose: 5000,
                  icon: "❌",
                }
              );
            }
          } finally {
            setIsAnalyzing(false);
            console.log("✅ Rental background processing completed for internalRequestId:", internalRequestId);
          }
        })();

        // 📌 IMPORTANT: Don't wait for background processing
      } catch (immediateError) {
        debugLog("Rental immediate error", immediateError);
        setError(immediateError);
        setIsAnalyzing(false);

        if (immediateError.reason === "limit_reached") {
          throw immediateError;
        } else if (immediateError.code === ManusApiErrors.API_KEY_MISSING) {
          toast.error(
            "Thiếu Manus API key. Vui lòng cấu hình REACT_APP_MANUS_API_KEY.",
            {
              position: "top-right",
              autoClose: 5000,
            }
          );
        } else {
          toast.error(immediateError.message || "Lỗi khi gửi yêu cầu", {
            position: "top-right",
            autoClose: 5000,
            icon: "❌",
          });
        }
      }
      // ✅ REMOVED FINALLY BLOCK - Không cần cleanup logic nữa
      // Usage chỉ được trừ KHI request thành công hoàn toàn trong background processing
    },
    [formData]
  );

  return {
    isAnalyzing,
    analysisProgress,
    manusResponse,
    uploadedFiles,
    handleAnalyzeRentalPrice,
    resetAnalysis,
    cleanupWebSocket: () => {},
    currentRequestId,
    currentTaskId,

    // Compatibility fields
    isFirebaseTracking: isAnalyzing,
    firebaseProgress: analysisProgress,
    firebaseStatus: taskStatus,
    firebaseFiles: uploadedFiles,
    firebaseError: error,
    isCompleted: taskStatus === "completed",
    isFailed: taskStatus === "failed",
    startTracking: () => {},
    stopTracking: () => {},
    resetTracking: () => {},
  };
};

export default useManusRentalAnalysis;

